// Platform seam for the OIDC redirect step — the ONLY part of the auth flow
// that differs between the browser-extension build and the web build.
//
// Everything security-sensitive (PKCE generation, state/CSRF check, token
// exchange, refresh, session storage) lives in auth.ts and is shared verbatim
// by both targets. A driver only answers two questions:
//   1. what redirect_uri do we register/use?
//   2. how do we bounce the user to Entra and get the callback URL back?
//
// The active driver is picked at COMPILE time via __PLATFORM__ (Vite define),
// so a given build ships exactly one driver — they never coexist at runtime.

export interface AuthDriver {
  /** redirect_uri sent to Entra in both /authorize and the token exchange.
   *  Single source so the two can't drift (a mismatch is rejected by Entra). */
  redirectUri(): string;

  /**
   * Begin the authorize flow.
   * - Extension: opens launchWebAuthFlow and RESOLVES with the callback URL in
   *   the same context, so loginEpfl() can finish inline.
   * - Web: navigates the tab to Entra and NEVER resolves — the flow continues
   *   on the /auth/callback page, which calls finishLogin() itself.
   */
  start(authUrl: string): Promise<string>;

  /**
   * Same authorize flow, but with ZERO user-visible UI — used for `prompt=none`
   * renewals (see silentLogin in auth.ts). Resolves with the callback URL when
   * Entra could answer from its own browser session. REJECTS, with a short
   * reason, when it would need to show something (no session, MFA due, consent
   * missing) or got no answer at all; silentLogin() turns that into "ask the
   * user to sign in" and keeps the reason for diagnosis.
   */
  startSilent(authUrl: string): Promise<string>;

  /**
   * Top-level `prompt=none` renewal: navigate the current page to Entra, which
   * comes straight back to the callback with a code when it can answer from
   * its session. First-party, so it works where the hidden iframe of
   * startSilent() does not (third-party cookies blocked). Returns false when
   * the target has no page to navigate: the extension renews through
   * launchWebAuthFlow, which is already first-party.
   */
  silentRedirect(authUrl: string): boolean;
}

/** Give up on a silent renewal after this long — a hung iframe must not leave
 *  the caller waiting forever mid-chat. */
const SILENT_TIMEOUT_MS = 20_000;

const extensionAuthDriver: AuthDriver = {
  redirectUri() {
    // Computed at runtime so it works regardless of the assigned extension ID
    // (local dev key vs Web Store ID). Must be registered in the EPFL portal.
    return chrome.identity.getRedirectURL();
  },
  start(authUrl) {
    return new Promise<string>((resolve, reject) => {
      chrome.identity.launchWebAuthFlow(
        { url: authUrl, interactive: true },
        (responseUrl) => {
          if (chrome.runtime.lastError || !responseUrl) {
            reject(new Error(chrome.runtime.lastError?.message ?? "Login cancelled"));
            return;
          }
          resolve(responseUrl);
        },
      );
    });
  },
  startSilent(authUrl) {
    // interactive:false never paints a window: Chrome loads the URL headlessly
    // and only resolves if it lands back on the redirect URI. Cookies come from
    // the normal profile jar, so an existing EPFL browser session is reused.
    // Chrome ≥113 kills a non-interactive flow as soon as the FIRST page has
    // loaded without reaching the redirect URI (abortOnLoadForNonInteractive,
    // default true). Entra often hops through an intermediate JS page before
    // its final 302, which that default turns into "User interaction required"
    // even though Entra would have answered. Let the flow run, bounded by our
    // own timeout. @types/chrome 0.0.287 predates both fields, hence the cast.
    const details = {
      url: authUrl,
      interactive: false,
      abortOnLoadForNonInteractive: false,
      timeoutMsForNonInteractive: SILENT_TIMEOUT_MS,
    } as Parameters<typeof chrome.identity.launchWebAuthFlow>[0];
    return new Promise<string>((resolve, reject) => {
      chrome.identity.launchWebAuthFlow(
        details,
        (responseUrl) => {
          // lastError must be read even when ignored, or Chrome logs it as
          // unchecked. "User interaction required" lands here — expected, not
          // a bug, but its wording is the only clue to what Entra wanted.
          const failed = chrome.runtime.lastError;
          if (failed || !responseUrl) {
            reject(new Error(failed?.message ?? "launchWebAuthFlow: no response URL"));
          } else {
            resolve(responseUrl);
          }
        },
      );
    });
  },
  silentRedirect() {
    return false;
  },
};

const webAuthDriver: AuthDriver = {
  redirectUri() {
    // Same-origin callback page served by the SPA. Must be registered as a
    // Web (SPA) redirect URI in the EPFL Entra app.
    return `${location.origin}/auth/callback`;
  },
  start(authUrl) {
    // Full-page redirect: the tab navigates away and this promise never settles.
    // {verifier,state} were stashed in sessionStorage by loginEpfl() and are
    // read back on the callback page by finishLogin().
    location.assign(authUrl);
    return new Promise<string>(() => {});
  },
  startSilent(authUrl) {
    // Hidden iframe, the classic OIDC silent-renew: Entra answers a prompt=none
    // request inside a frame and redirects to our same-origin /auth/callback,
    // which posts the URL back up (see sidepanel/main.ts). If it would need UI
    // it redirects with error=login_required instead — also a callback URL, and
    // finishLogin() turns that into a rejection the caller reads as "not silent".
    // Third-party-cookie blocking simply makes this time out → reject → re-login.
    return new Promise<string>((resolve, reject) => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      let settled = false;
      const finish = (url: string | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        iframe.remove();
        if (url) resolve(url);
        else {
          reject(
            new Error(
              `no answer from Microsoft in ${SILENT_TIMEOUT_MS / 1000}s (hidden iframe blocked or third-party cookies refused?)`,
            ),
          );
        }
      };
      const onMessage = (e: MessageEvent) => {
        if (e.origin !== location.origin) return;
        const data = e.data as { type?: string; url?: string } | null;
        if (data?.type === "oidc-silent-callback" && typeof data.url === "string") {
          finish(data.url);
        }
      };
      const timer = setTimeout(() => finish(null), SILENT_TIMEOUT_MS);
      window.addEventListener("message", onMessage);
      iframe.src = authUrl;
      document.body.appendChild(iframe);
    });
  },
  silentRedirect(authUrl) {
    // Same full-page redirect as start(): the PKCE stash is in sessionStorage,
    // the /auth/callback page finishes the exchange, then returns to the shell
    // noted in app.returnTo.
    location.assign(authUrl);
    return true;
  },
};

export const authDriver: AuthDriver =
  __PLATFORM__ === "web" ? webAuthDriver : extensionAuthDriver;
